// Путь: D:\QR\QR-WEB\dashboard\src\components\Dashboard\BusinessDashboard.js
// Название: Business Dashboard - Synced Data
// Версия: 8.0.0
// Дата: 27.01.2026

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://qr-money.onrender.com/api';

const BusinessDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [wallet, setWallet] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [stats, setStats] = useState({ leads: 0, campaigns: 0, active: 0, completed: 0 });

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: 'Bearer ' + token };

      const [walletRes, campaignsRes] = await Promise.all([
        fetch(`${API_URL}/brand-wallet`, { headers }),
        fetch(`${API_URL}/campaigns?type=brand`, { headers })
      ]);

      if (walletRes.ok) {
        const data = await walletRes.json();
        setWallet(data.wallet);
      }

      if (campaignsRes.ok) {
        const data = await campaignsRes.json();
        const list = data.data || data.campaigns || [];
        setCampaigns(list.slice(0, 3));
        
        // Подсчёт статистики
        const totalLeads = list.reduce((sum, c) => {
          const recipients = parseInt(c.recipients_count) || 0;
          return sum + recipients;
        }, 0);
        
        setStats({
          campaigns: list.length,
          active: list.filter(c => c.status === 'ready' || c.status === 'active' || c.status === 'generating').length,
          completed: list.filter(c => c.status === 'completed').length,
          leads: totalLeads
        });
      }
    } catch (err) {
      console.error('Dashboard error:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatAmount = (val) => {
    return parseFloat(val || 0).toLocaleString('ru-RU', { 
      minimumFractionDigits: 2, maximumFractionDigits: 2 
    });
  };

  const getStatusText = (status) => {
    const map = {
      'ready': 'Активна',
      'active': 'Активна',
      'generating': 'Генерация',
      'completed': 'Завершена',
      'expired': 'Истекла'
    };
    return map[status] || status;
  };

  const getStatusColor = (status) => {
    if (status === 'ready' || status === 'active') return { bg: '#dcfce7', color: '#166534' };
    if (status === 'completed') return { bg: '#dbeafe', color: '#1e40af' };
    if (status === 'generating') return { bg: '#fef3c7', color: '#92400e' };
    return { bg: '#f3f4f6', color: '#6b7280' };
  };

  // Получаем количество лидов для кампании
  const getCampaignLeads = (campaign) => {
    return parseInt(campaign.recipients_count) || 0;
  };

  // Получаем лимит лидов из brand_config
  const getCampaignLeadLimit = (campaign) => {
    return campaign.brand_config?.leadCount || campaign.brand_config?.giftCount || 0;
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loading}>⏳ Загрузка...</div>
      </div>
    );
  }

  const totalReserved = parseFloat(wallet?.fees_reserve || 0);
  const hasCampaigns = campaigns.length > 0;

  return (
    <div style={s.page}>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.title}>👋 Добро пожаловать!</h1>
          <p style={s.subtitle}>DARY Business — платформа лидогенерации</p>
        </div>
        <button style={s.refreshBtn} onClick={loadData}>🔄 Обновить</button>
      </div>

      {/* Stats Cards */}
      <div style={s.statsGrid}>
        <div style={{...s.statCard, ...s.statPurple}}>
          <div style={s.statIcon}>💰</div>
          <div style={s.statContent}>
            <div style={s.statValue}>€{formatAmount(wallet?.available_balance)}</div>
            <div style={s.statLabel}>Доступный баланс</div>
          </div>
        </div>
        <div style={{...s.statCard, ...s.statGreen}}>
          <div style={s.statIcon}>📦</div>
          <div style={s.statContent}>
            <div style={s.statValue}>{stats.campaigns}</div>
            <div style={s.statLabel}>Кампаний</div>
          </div>
        </div>
        <div style={{...s.statCard, ...s.statBlue}}>
          <div style={s.statIcon}>🎯</div>
          <div style={s.statContent}>
            <div style={s.statValue}>{stats.leads}</div>
            <div style={s.statLabel}>Лидов получено</div>
          </div>
        </div>
        <div style={{...s.statCard, ...s.statOrange}}>
          <div style={s.statIcon}>⏳</div>
          <div style={s.statContent}>
            <div style={s.statValue}>€{formatAmount(totalReserved)}</div>
            <div style={s.statLabel}>В работе</div>
          </div>
        </div>
      </div>

      <div style={s.mainGrid}>
        {/* Left Column */}
        <div>
          {/* Quick Actions */}
          <div style={s.card}>
            <h3 style={s.cardTitle}>⚡ Быстрые действия</h3>
            <div style={s.actionsGrid}>
              <button style={{...s.actionBtn, ...s.actionPrimary}} onClick={() => navigate('/business/brand/create')}>
                <span style={{fontSize: 24}}>➕</span>
                <span>Создать кампанию</span>
              </button>
              <button style={s.actionBtn} onClick={() => navigate('/business/wallet')}>
                <span style={{fontSize: 24}}>💳</span>
                <span>Пополнить</span>
              </button>
              <button style={s.actionBtn} onClick={() => navigate('/business/leads')}>
                <span style={{fontSize: 24}}>👥</span>
                <span>Лиды</span>
              </button>
              <button style={s.actionBtn} onClick={() => navigate('/business/brand/analytics')}>
                <span style={{fontSize: 24}}>📊</span>
                <span>Аналитика</span>
              </button>
            </div>
          </div>

          {/* Wallet Summary */}
          <div style={s.card}>
            <div style={s.cardHeader}>
              <h3 style={s.cardTitle}>💰 Brand Wallet</h3>
              <button style={s.linkBtn} onClick={() => navigate('/business/wallet')}>Управление →</button>
            </div>
            {wallet ? (
              <div style={s.walletGrid}>
                <div style={s.walletItem}>
                  <div style={{...s.walletValue, color: '#8B5CF6'}}>€{formatAmount(wallet.deposit_balance)}</div>
                  <div style={s.walletLabel}>Депозит</div>
                </div>
                <div style={s.walletItem}>
                  <div style={{...s.walletValue, color: '#10B981'}}>€{formatAmount(wallet.available_balance)}</div>
                  <div style={s.walletLabel}>Доступно</div>
                </div>
                <div style={s.walletItem}>
                  <div style={{...s.walletValue, color: '#F59E0B'}}>€{formatAmount(totalReserved)}</div>
                  <div style={s.walletLabel}>В кампаниях</div>
                </div>
                <div style={s.walletItem}>
                  <div style={{...s.walletValue, color: '#6B7280'}}>€{formatAmount(wallet.total_fees_paid)}</div>
                  <div style={s.walletLabel}>Оплачено</div>
                </div>
              </div>
            ) : (
              <p style={{color: '#9ca3af', textAlign: 'center', padding: 20}}>Кошелёк не найден</p>
            )}
          </div>
        </div>

        {/* Right Column */}
        <div>
          {/* Campaigns or CTA */}
          {hasCampaigns ? (
            <div style={s.card}>
              <div style={s.cardHeader}>
                <h3 style={s.cardTitle}>📦 Последние кампании</h3>
                <button style={s.linkBtn} onClick={() => navigate('/business/brand/campaigns')}>Все →</button>
              </div>
              <div style={s.campaignsList}>
                {campaigns.map(c => {
                  const leads = getCampaignLeads(c);
                  const limit = getCampaignLeadLimit(c);
                  const statusStyle = getStatusColor(c.status);
                  return (
                    <div key={c.id} style={s.campaignItem} onClick={() => navigate(`/business/brand/giveaway/${c.id}/wizard`)}>
                      <div style={s.campaignInfo}>
                        <div style={s.campaignName}>{c.name || `Кампания #${c.id}`}</div>
                        <div style={s.campaignMeta}>
                          <span style={{...s.badge, background: statusStyle.bg, color: statusStyle.color}}>
                            {getStatusText(c.status)}
                          </span>
                          <span style={s.campaignDate}>{leads}/{limit} лидов</span>
                        </div>
                      </div>
                      <div style={s.campaignArrow}>→</div>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div style={s.ctaCard}>
              <div style={s.ctaIcon}>🚀</div>
              <h3 style={s.ctaTitle}>Начните получать лидов!</h3>
              <p style={s.ctaText}>Создайте первую кампанию и привлекайте клиентов с помощью AI-подарков</p>
              <button style={s.ctaBtn} onClick={() => navigate('/business/brand/create')}>
                Создать кампанию →
              </button>
            </div>
          )}

          {/* Pricing Info */}
          <div style={s.infoCard}>
            <h4 style={s.infoTitle}>💡 Как это работает</h4>
            <div style={s.infoList}>
              <div style={s.infoItem}>
                <span style={s.infoIcon}>1️⃣</span>
                <span>Создайте кампанию и укажите количество лидов</span>
              </div>
              <div style={s.infoItem}>
                <span style={s.infoIcon}>2️⃣</span>
                <span>AI генерирует уникальные подарки-картинки</span>
              </div>
              <div style={s.infoItem}>
                <span style={s.infoIcon}>3️⃣</span>
                <span>Раздайте подарки клиентам</span>
              </div>
              <div style={s.infoItem}>
                <span style={s.infoIcon}>4️⃣</span>
                <span>Получайте верифицированных лидов по €20/лид</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Styles
const s = {
  page: { padding: 24, maxWidth: 1200, margin: '0 auto', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  loading: { textAlign: 'center', padding: 80, fontSize: 18, color: '#6b7280' },
  
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 },
  title: { fontSize: 28, fontWeight: 700, color: '#1f2937', marginBottom: 4 },
  subtitle: { fontSize: 15, color: '#6b7280', margin: 0 },
  refreshBtn: { padding: '10px 20px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 10, cursor: 'pointer', fontSize: 14 },

  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 },
  statCard: { display: 'flex', alignItems: 'center', gap: 16, padding: 20, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.08)' },
  statPurple: { background: 'linear-gradient(135deg, #f3e8ff 0%, #e9d5ff 100%)', border: '1px solid #c4b5fd' },
  statGreen: { background: 'linear-gradient(135deg, #dcfce7 0%, #bbf7d0 100%)', border: '1px solid #86efac' },
  statBlue: { background: 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)', border: '1px solid #93c5fd' },
  statOrange: { background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)', border: '1px solid #fcd34d' },
  statIcon: { fontSize: 32 },
  statContent: {},
  statValue: { fontSize: 24, fontWeight: 800, color: '#1f2937' },
  statLabel: { fontSize: 13, color: '#6b7280', marginTop: 2 },

  // Main Grid
  mainGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 24 },
  
  // Cards
  card: { background: 'white', borderRadius: 20, padding: 24, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', marginBottom: 20 },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 },
  cardTitle: { fontSize: 16, fontWeight: 700, color: '#1f2937', margin: 0 },
  linkBtn: { background: 'none', border: 'none', color: '#8B5CF6', fontSize: 14, fontWeight: 600, cursor: 'pointer' },

  // Actions
  actionsGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 12 },
  actionBtn: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: 20, background: '#f9fafb', border: '2px solid #e5e7eb', borderRadius: 16, cursor: 'pointer', fontSize: 14, fontWeight: 600, color: '#374151', transition: 'all 0.2s' },
  actionPrimary: { background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', border: 'none', color: 'white' },

  // Wallet
  walletGrid: { display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 },
  walletItem: { textAlign: 'center', padding: 16, background: '#f9fafb', borderRadius: 12 },
  walletValue: { fontSize: 22, fontWeight: 800, marginBottom: 4 },
  walletLabel: { fontSize: 12, color: '#6b7280', textTransform: 'uppercase' },

  // Campaigns
  campaignsList: {},
  campaignItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: '1px solid #f3f4f6', cursor: 'pointer' },
  campaignInfo: {},
  campaignName: { fontSize: 15, fontWeight: 600, color: '#1f2937', marginBottom: 6 },
  campaignMeta: { display: 'flex', alignItems: 'center', gap: 10 },
  badge: { padding: '4px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600 },
  campaignDate: { fontSize: 13, color: '#9ca3af' },
  campaignArrow: { fontSize: 18, color: '#9ca3af' },

  // CTA
  ctaCard: { background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)', borderRadius: 20, padding: 32, textAlign: 'center', border: '2px dashed #7dd3fc', marginBottom: 20 },
  ctaIcon: { fontSize: 48, marginBottom: 16 },
  ctaTitle: { fontSize: 20, fontWeight: 700, color: '#1f2937', marginBottom: 8 },
  ctaText: { fontSize: 14, color: '#6b7280', marginBottom: 20, lineHeight: 1.5 },
  ctaBtn: { padding: '14px 32px', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: 'white', border: 'none', borderRadius: 12, fontWeight: 700, cursor: 'pointer', fontSize: 15 },

  // Info
  infoCard: { background: '#fffbeb', borderRadius: 16, padding: 20, border: '1px solid #fcd34d' },
  infoTitle: { fontSize: 14, fontWeight: 700, color: '#92400e', marginBottom: 16 },
  infoList: {},
  infoItem: { display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 12, fontSize: 13, color: '#78350f', lineHeight: 1.4 },
  infoIcon: { flexShrink: 0 }
};

export default BusinessDashboard;